import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';

// Pull the most recent company notes for joining against completed tasks in
// bottleneck detection. Capped so this stays a light, single subscription.
const NOTES_LIMIT = 500;

/**
 * Subscribes to recent non-archived taskNotes across the company. Notes are
 * joined to the completed-task pool by taskId in the bottleneck service, so
 * only notes belonging to in-period tasks contribute.
 *
 * Degrades gracefully: if the composite index is not deployed yet the query
 * errors, the hook returns an empty list and the recurring-issue bottleneck
 * simply does not appear — the rest of detection is unaffected.
 *
 * Requires composite index: companyId (asc) + archived (asc) + createdAt (desc).
 */
export function useCompanyTaskNotes() {
  const { companyId } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }

    const q = query(
      collection(db, COLLECTIONS.TASK_NOTES),
      where('companyId', '==', companyId),
      where('archived', '==', false),
      orderBy('createdAt', 'desc'),
      limit(NOTES_LIMIT),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        // Most likely a missing/undeployed composite index — fail soft.
        console.warn('[useCompanyTaskNotes] notes query unavailable:', err?.message || err);
        setNotes([]);
        setLoading(false);
      },
    );

    return unsub;
  }, [companyId]);

  return { notes, loading };
}
